// @spec DHB-STR-022
'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { FieldMappingDialog } from '@/features/datahub/integrations/components/field-mapping-dialog';
import { FieldMappingTable } from '@/features/datahub/integrations/components/field-mapping-table';
import type { FieldMapping, IntegrationDetail } from '@/features/datahub/integrations/types';
import { useOrganizationId } from '@/hooks/use-organization-id';
import { useOrgRole } from '@/hooks/use-org-role';
import { createApiClient } from '@/lib/api-client';
import i18n from '@/lib/i18n';

export const Route = createFileRoute(
  '/_authenticated/datahub/integrations/$integrationId/mappings'
)({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.fieldMappings'),
  }),
  component: FieldMappingsPage,
});

/**
 * Field mappings page for a specific integration.
 * Shows a table of field mappings with create/edit/delete functionality.
 */
function FieldMappingsPage() {
  const { integrationId } = Route.useParams();
  const orgId = useOrganizationId();
  const { getToken } = useAuth();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();
  const [selectedMapping, setSelectedMapping] = useState<FieldMapping | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch integration details for breadcrumb
  const { data: integration, isLoading: isLoadingIntegration } = useQuery({
    queryKey: ['organizations', orgId, 'datahub-integration', integrationId],
    queryFn: async () => {
      if (!orgId) {
        throw new Error('Organization ID not available');
      }
      const apiClient = createApiClient({ getAuthToken: getToken });
      return apiClient.get<IntegrationDetail>(
        `/organizations/${orgId}/datahub/integrations/${integrationId}`
      );
    },
    enabled: !!orgId && !!integrationId,
  });

  // Handle edit click from table
  const handleEdit = (mapping: FieldMapping) => {
    setSelectedMapping(mapping);
    setDialogOpen(true);
  };

  // Handle add new click
  const handleAddNew = () => {
    setSelectedMapping(null);
    setDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setSelectedMapping(null);
    }
  };

  // Show skeleton while loading
  if (isLoadingRole || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/datahub">Data Hub</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <ChevronRight className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/datahub/integrations">Integrations</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <ChevronRight className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              {isLoadingIntegration ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                <BreadcrumbLink asChild>
                  <Link to="/datahub/integrations">{integration?.name ?? 'Integration'}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <ChevronRight className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>Field Mappings</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">Field Mappings</h1>
            <p className="mt-1 text-muted-foreground">
              {isLoadingIntegration ? (
                <Skeleton className="h-5 w-64 inline-block" />
              ) : (
                <>
                  Configure how data from {integration?.name ?? 'this integration'} maps to your
                  datapoints
                </>
              )}
            </p>
          </div>
          {isAdmin && (
            <Button onClick={handleAddNew}>
              <Plus className="mr-2 h-4 w-4" />
              Add Mapping
            </Button>
          )}
        </header>

        {/* Field mappings table */}
        <FieldMappingTable integrationId={integrationId} onEdit={handleEdit} isAdmin={isAdmin} />
      </div>

      {/* Create/Edit dialog */}
      <FieldMappingDialog
        integrationId={integrationId}
        mapping={selectedMapping}
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
      />
    </div>
  );
}
